import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const FIXED_EMOJIS = ["🔥", "❤️", "👑", "🎧", "💯", "🚀"];

/**
 * TrackReactionBar — Facebook-style reaction strip directly on a track.
 * One toggle per emoji per user. Audio is unaffected (this is a sibling
 * component to AudioPlayer, never unmounts it).
 */
const TrackReactionBar = ({ submissionId, currentUser }) => {
  const [counts, setCounts] = useState({});
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyEmoji, setBusyEmoji] = useState(null);

  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/track/${submissionId}/reactions`)
      .then((r) => {
        if (cancelled) return;
        setCounts(r.data?.counts || {});
        setMine(r.data?.mine || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [submissionId]);

  const handleToggle = async (emoji) => {
    if (!currentUser) {
      toast.error("Sign in to react");
      return;
    }
    if (busyEmoji) return;
    // Optimistic toggle so the bar feels instant
    const wasMine = mine.includes(emoji);
    const optimisticCounts = {
      ...counts,
      [emoji]: Math.max(0, (counts[emoji] || 0) + (wasMine ? -1 : 1)),
    };
    setMine(wasMine ? mine.filter((e) => e !== emoji) : [...mine, emoji]);
    setCounts(optimisticCounts);
    setBusyEmoji(emoji);
    try {
      const r = await api.post(
        `/track/${submissionId}/reactions/toggle`,
        { emoji },
      );
      // Trust server response over optimistic state
      setCounts(r.data?.counts || optimisticCounts);
      setMine(r.data?.mine || []);
    } catch (err) {
      // Revert on failure
      setMine(wasMine ? [...mine, emoji] : mine.filter((e) => e !== emoji));
      setCounts(counts);
      toast.error(err.response?.data?.detail || "Could not react");
    } finally {
      setBusyEmoji(null);
    }
  };

  return (
    <div
      className="flex flex-wrap gap-2 mt-3"
      data-testid="track-reaction-bar"
      role="group"
      aria-label="Track reactions"
    >
      {FIXED_EMOJIS.map((emoji) => {
        const isMine = mine.includes(emoji);
        const count = counts[emoji] || 0;
        return (
          <button
            key={emoji}
            type="button"
            onClick={() => handleToggle(emoji)}
            disabled={loading || busyEmoji === emoji}
            className={`min-h-[40px] px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 border transition-all hover:scale-110 active:scale-95 ${
              isMine
                ? "bg-gradient-to-r from-purple-600/40 to-pink-600/40 border-purple-400/70 text-white shadow-lg shadow-purple-500/20"
                : "bg-black/40 border-gray-700 text-gray-300 hover:border-purple-500/60 hover:text-white"
            } ${busyEmoji === emoji ? "opacity-60" : ""}`}
            data-testid={`track-reaction-${emoji}`}
            aria-pressed={isMine}
            aria-label={`${emoji} ${count} reactions${isMine ? ' (active)' : ''}`}
          >
            <span className="text-lg leading-none">{emoji}</span>
            <span className="font-semibold tabular-nums">{count}</span>
          </button>
        );
      })}
    </div>
  );
};

export default TrackReactionBar;
